import { CompanyAuthenticationTabWebComponent } from '../component/CompanyAuthenticationTabWebComponent';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { CompanyAuthenticationTabView } from '../view/CompanyAuthenticationTabView';
import { templateCompanyAuthenticationTab } from '../view/template';
import { CompanyAuthenticationTabRepository } from '../repository/CompanyAuthenticationTabRepository';
import { MyCompanyAuthenticationData } from '../../../types/company-data';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { EditCompanyPanelEvents } from '../../../editCompany/editCompanyPanel/view/EditCompanyPanelEnums';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { CompanyEditingRight } from '../../types/CompanyEditingRight';
import { OnCompanyPanelClose } from '../../events/CompanyPanelEvent';

export class CompanyAuthenticationTabController extends BaseWebComponentController {
  public component: CompanyAuthenticationTabWebComponent;
  public dataRepository: CompanyAuthenticationTabRepository;
  public dataRepositoryConstructor: typeof CompanyAuthenticationTabRepository;
  public view: CompanyAuthenticationTabView;
  public viewConstructor: typeof CompanyAuthenticationTabView;

  constructor(component: CompanyAuthenticationTabWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompanyAuthenticationTabView,
      dataRepositoryConstructor: CompanyAuthenticationTabRepository,
      viewTemplate: templateCompanyAuthenticationTab,
    });
  }

  async getDataForView(): Promise<{
    authData: MyCompanyAuthenticationData;
    editingRight: CompanyEditingRight;
  }> {
    return {
      authData: await this.dataRepository.getMyCompanyAuthenticationData(this.component.companyId),
      editingRight: this.component.editingRight,
    };
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.emitCloseCompanyWindowEvent,
      },
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.save,
      },
    ];
  }

  private async save() {
    if (this.view.dom.$authenticationForm.hasRemovedSsoProvider()) {
      return this.view.dom.$authenticationForm.showIdentityProviderConfirmationWindow(this.saveSettings.bind(this));
    }

    return await this.saveSettings();
  }

  private async saveSettings() {
    this.view.dom.$saveButton?.setLoadingState(true);

    try {
      this.dispatchLoadingEvent(true);

      await this.dependencies.api.clients.Company.SetAuthenticationSettings(
        this.component.companyId,
        this.view.formGroup.value
      ).toPromise();

      const authenticationFormReference = this.view.dom.$authenticationForm;
      if (authenticationFormReference) {
        authenticationFormReference.updateCachedFields();
        authenticationFormReference.refreshInitialFormValue();
      }
      this.showSuccessToast();
    } catch (e) {
      this.showErrorToast(e);
    } finally {
      this.view.dom.$saveButton?.setLoadingState(false);
      this.dispatchLoadingEvent(false);
    }
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Authentication details updated successfully.'),
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(error: Error) {
    const errorMessage = error?.message || this.translator.getGenericErrorMessage();

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  private emitCloseCompanyWindowEvent() {
    this.component.dispatchEvent(new OnCompanyPanelClose());
  }

  private dispatchLoadingEvent(operationInProgress: boolean) {
    if (this.component.formContext !== CompanyActionContext.Edit) {
      return;
    }

    // Dispatch event only when editing a company
    this.component.dispatchEvent(
      new CustomEvent(EditCompanyPanelEvents.AuthenticationTabOperationInProgress, {
        detail: { isOperationInProgress: operationInProgress },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }
}
